import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { DraftingCompass, Building2, Mountain, Landmark } from "lucide-react";
import { scrollToSection } from "@/components/Navbar";

export const requestEstimate = (projectType) => {
  if (projectType) {
    window.dispatchEvent(new CustomEvent("veritas:project-type", { detail: projectType }));
  }
  scrollToSection("#contact");
};

const services = [
  {
    n: "01",
    title: "Development",
    icon: DraftingCompass,
    desc: "From raw land to finished community.",
    offerings: ["Land Acquisition", "Feasibility", "Entitlements", "Multifamily", "Mixed-Use", "Commercial Development"],
    type: "Real Estate Development",
  },
  {
    n: "02",
    title: "Construction",
    icon: Building2,
    desc: "One accountable team, from plans to keys.",
    offerings: ["General Construction", "Construction Management", "Commercial & Multifamily", "New Homes", "Major Renovations"],
    type: "Commercial Construction",
  },
  {
    n: "03",
    title: "Site Development",
    icon: Mountain,
    desc: "Groundwork done right the first time.",
    offerings: ["Excavation", "Concrete", "Grading", "Drainage", "Site Preparation", "Exterior Improvements"],
    type: "Excavation / Site Work",
  },
  {
    n: "04",
    title: "Capital Advisory",
    icon: Landmark,
    desc: "Financing structured around the deal.",
    offerings: ["Construction Financing", "Acquisition Financing", "Bridge Financing", "Development Capital"],
    financing: true,
  },
];

const capabilities = [
  { label: "Residential Construction", type: "New Construction" },
  { label: "Concrete", type: "Concrete" },
  { label: "Excavation", type: "Excavation / Site Work" },
  { label: "Renovations", type: "Renovation" },
  { label: "Landscape Construction", type: "Landscape / Property Improvement" },
];

const ServiceCard = ({ s, i }) => {
  const Icon = s.icon;
  const navigate = useNavigate();
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay: (i % 2) * 0.12, ease: [0.16, 1, 0.3, 1] }}
      className="group relative border border-[#D4AF37]/15 bg-[#0A0A0A] p-8 sm:p-10 overflow-hidden hover:border-[#D4AF37]/45 transition-colors duration-500 flex flex-col"
      data-testid={`service-card-${s.n}`}
    >
      <span className="absolute -top-6 right-4 font-display text-[7rem] sm:text-[9rem] leading-none text-[#D4AF37]/[0.07] group-hover:text-[#D4AF37]/[0.16] transition-colors duration-700 select-none">
        {s.n}
      </span>
      <div className="relative flex-1">
        <span className="inline-flex items-center justify-center w-11 h-11 border border-[#D4AF37]/35 text-[#D4AF37] mb-7 group-hover:bg-[#D4AF37] group-hover:text-black transition-colors duration-500">
          <Icon size={19} strokeWidth={1.25} />
        </span>
        <h3 className="font-display text-2xl sm:text-3xl font-medium tracking-wide text-[#F5F5F5]">
          {s.title}
        </h3>
        <span className="block w-10 h-px bg-[#D4AF37]/60 mt-5 mb-5 group-hover:w-20 transition-[width] duration-500" />
        <p className="text-sm sm:text-base font-light leading-relaxed tracking-wide text-[#A3A3A3] mb-7">
          {s.desc}
        </p>
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5" data-testid={`service-card-${s.n}-offerings`}>
          {s.offerings.map((o) => (
            <li key={o} className="flex items-baseline gap-3 text-[13px] tracking-wide text-[#F5F5F5]/90">
              <span className="w-1.5 h-1.5 rotate-45 bg-[#D4AF37] shrink-0" />
              {o}
            </li>
          ))}
        </ul>
      </div>
      <div className="relative mt-8 pt-6 border-t border-[#D4AF37]/10">
        {s.financing ? (
          <button
            onClick={() => navigate("/financing")}
            data-testid={`service-card-${s.n}-estimate-button`}
            className="w-full sm:w-auto bg-[#D4AF37] text-black px-7 py-3.5 text-[10px] tracking-[0.28em] uppercase font-medium hover:bg-[#F3E5AB] transition-colors duration-300"
          >
            Explore Financing
          </button>
        ) : (
          <button
            onClick={() => requestEstimate(s.type)}
            data-testid={`service-card-${s.n}-estimate-button`}
            className="w-full sm:w-auto bg-[#D4AF37] text-black px-7 py-3.5 text-[10px] tracking-[0.28em] uppercase font-medium hover:bg-[#F3E5AB] transition-colors duration-300"
          >
            Request an Estimate
          </button>
        )}
      </div>
    </motion.article>
  );
};

export default function Services() {
  return (
    <section id="services" className="relative py-28 lg:py-40 border-t border-[#D4AF37]/10" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-16 lg:mb-24 lg:flex lg:items-end lg:justify-between">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              className="text-[11px] tracking-[0.45em] uppercase text-[#D4AF37] mb-8"
              data-testid="services-overline"
            >
              02 — What We Do
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-[#F5F5F5]"
              data-testid="services-headline"
            >
              Four divisions.
              <br />
              <span className="italic text-[#D4AF37]">One standard.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-8 lg:mt-0 max-w-sm text-sm font-light leading-relaxed tracking-wide text-[#A3A3A3]"
          >
            A development and construction organization — four divisions
            working as one, with the trades that build held in-house to a
            single standard.
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {services.map((s, i) => (
            <ServiceCard key={s.n} s={s} i={i} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9 }}
          className="mt-12 lg:mt-14 border border-[#D4AF37]/15 bg-[#080808] p-8 sm:p-10"
          data-testid="capabilities-strip"
        >
          <p className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6" data-testid="capabilities-overline">
            In-House Capabilities
          </p>
          <div className="flex flex-wrap gap-3">
            {capabilities.map((c) => (
              <button
                key={c.label}
                onClick={() => requestEstimate(c.type)}
                data-testid={`capability-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="border border-[#D4AF37]/30 text-[#F5F5F5] px-6 py-3 text-[11px] tracking-[0.24em] uppercase hover:bg-[#D4AF37] hover:text-black hover:border-[#D4AF37] transition-colors duration-300"
              >
                {c.label}
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
